/**
 * Performance monitoring utilities for image preloading
 */

export interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private observers: PerformanceObserver[] = [];

  /**
   * Start monitoring core web vitals and image loading performance
   */
  startMonitoring() {
    // Monitor Largest Contentful Paint (LCP)
    this.observePerformanceEntry('largest-contentful-paint', (entry) => {
      this.addMetric('LCP', entry.startTime, {
        element: entry.element?.tagName?.toLowerCase(),
        url: entry.url,
      });
    });

    // Monitor First Input Delay (FID)
    this.observePerformanceEntry('first-input', (entry) => {
      this.addMetric('FID', entry.processingStart - entry.startTime, {
        eventType: entry.name,
      });
    });

    // Monitor Cumulative Layout Shift (CLS)
    this.observePerformanceEntry('layout-shift', (entry) => {
      if (!entry.hadRecentInput) {
        this.addMetric('CLS', entry.value, {
          sources: entry.sources?.map((s: any) => ({
            element: s.node?.tagName?.toLowerCase(),
          })),
        });
      }
    });

    // Monitor resource loading (including images)
    this.observePerformanceEntry('resource', (entry) => {
      if (entry.initiatorType === 'img') {
        this.addMetric('ImageLoadTime', entry.duration, {
          url: entry.name,
          size: entry.transferSize,
          cached: entry.transferSize === 0,
        });
      }
    });
  }

  /**
   * Track image preloading metrics
   */
  trackImagePreload(url: string, loadTime: number, cached: boolean, error?: string) {
    this.addMetric('ImagePreloadTime', loadTime, {
      url,
      cached,
      error,
      success: !error,
    });
  }

  /**
   * Track color change performance
   */
  trackColorChange(color: string, imagesCount: number, totalTime: number) {
    this.addMetric('ColorChangeTime', totalTime, {
      color,
      imagesCount,
    });
  }

  /**
   * Get performance report
   */
  getReport() {
    const now = Date.now();
    const recentMetrics = this.metrics.filter(m => now - m.timestamp < 60000); // Last minute

    const report = {
      timestamp: now,
      metrics: {
        LCP: this.getAverageMetric('LCP'),
        FID: this.getAverageMetric('FID'),
        CLS: this.getSumMetric('CLS'),
        ImageLoadTime: this.getAverageMetric('ImageLoadTime'),
        ImagePreloadTime: this.getAverageMetric('ImagePreloadTime'),
        ColorChangeTime: this.getAverageMetric('ColorChangeTime'),
      },
      counts: {
        totalImages: this.getMetricCount('ImageLoadTime'),
        preloadedImages: this.getMetricCount('ImagePreloadTime'),
        cachedImages: this.getCachedImageCount(),
        colorChanges: this.getMetricCount('ColorChangeTime'),
      },
      recent: recentMetrics,
    };

    return report;
  }

  /**
   * Send performance data to analytics (placeholder)
   */
  sendToAnalytics() {
    const report = this.getReport();
    
    // Only log if there's meaningful data
    const hasData = report.counts.totalImages > 0 || report.counts.preloadedImages > 0;
    
    // Console log in development
    if (process.env.NODE_ENV === 'development' && hasData) {
      console.group('🚀 Image Preloading Performance Report');
      console.table(report.metrics);
      console.table(report.counts);
      console.groupEnd();
    }
  }

  private observePerformanceEntry(entryType: string, callback: (entry: any) => void) {
    try {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach(callback);
      });
      
      observer.observe({ entryTypes: [entryType] });
      this.observers.push(observer);
    } catch (error) {
      console.warn(`Failed to observe ${entryType}:`, error);
    }
  }

  private addMetric(name: string, value: number, metadata?: Record<string, any>) {
    this.metrics.push({
      name,
      value,
      timestamp: Date.now(),
      metadata,
    });

    // Keep only last 1000 metrics to prevent memory leaks
    if (this.metrics.length > 1000) {
      this.metrics = this.metrics.slice(-1000);
    }
  }

  private getAverageMetric(name: string): number {
    const metrics = this.metrics.filter(m => m.name === name);
    return metrics.length > 0 
      ? metrics.reduce((sum, m) => sum + m.value, 0) / metrics.length
      : 0;
  }

  private getSumMetric(name: string): number {
    return this.metrics
      .filter(m => m.name === name)
      .reduce((sum, m) => sum + m.value, 0);
  }

  private getMetricCount(name: string): number {
    return this.metrics.filter(m => m.name === name).length;
  }

  private getCachedImageCount(): number {
    return this.metrics
      .filter(m => m.name === 'ImagePreloadTime' && m.metadata?.cached)
      .length;
  }

  /**
   * Clean up observers
   */
  stopMonitoring() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

export const performanceMonitor = new PerformanceMonitor();

// Auto-start monitoring
if (typeof window !== 'undefined') {
  performanceMonitor.startMonitoring();
}